import dlv from 'dlv';

import {
  IBackendCartEntries,
  ICartEntry,
  IItem,
  IItemOptionModifier,
  IPluWithQuantity,
  IPluWithSize,
  IPrices,
  ISanityItemOptionModifier,
  ISanityVendorConfigs,
  IVendorConfig
} from '@rbi-ctg/menu';
import logger from 'utils/logger';

export enum PluTypes {
  CONSTANT = 'constantPlu',
  IGNORE = 'ignore',
  MULTI_CONSTANT = 'multiConstantPlus',
  PARENT_CHILD = 'parentChildPlu',
  QUANTITY = 'quantityBasedPlu',
  SIZE_BASED = 'sizeBasedPlu'
}

export enum PosVendors {
  NCR = 'ncr',
  NCR_DELIVERY = 'ncrDelivery',
  SICOM = 'sicom',
  SICOM_DELIVERY = 'sicomDelivery',
  CARROLLS = 'carrols',
  CARROLS_DELIVERY = 'carrolsDelivery',
  PRODUCT_NUMBER = 'productNumber',
  PRODUCT_NUMBER_DELIVERY = 'productNumberDelivery',
  QDI = 'qdi',
  QDI_DELIVERY = 'qdiDelivery',
  RPOS = 'rpos',
  RPOS_DELIVERY = 'rposDelivery',
  TABLET = 'tablet',
  TABLET_DELIVERY = 'tabletDelivery'
}

export const sortQuantityBasedPlus = (plus: IPluWithQuantity[]) => {
  return plus.slice().sort((left, right) => (left.quantity > right.quantity ? 1 : -1));
};

export const minQuantityPlu = (plus: IPluWithQuantity[] | undefined = []) => {
  const sorted = sortQuantityBasedPlus(plus);
  return sorted[0];
};

export interface IWithVendorConfig {
  _type?: string;
  type?: string;
  name?: { locale: string } | string;
  vendorConfigs?: ISanityVendorConfigs | null;
}

export const getVendorConfig = (
  item: IWithVendorConfig,
  vendor: PosVendors | null
): IVendorConfig | null | undefined => {
  if (!item || !item.vendorConfigs || !vendor) {
    return null;
  }

  const { vendorConfigs } = item;
  return vendorConfigs[vendor];
};

export const getConstantPlu = (vendorConfig: IVendorConfig | null | undefined): string | null => {
  if (!vendorConfig || vendorConfig.pluType !== PluTypes.CONSTANT) {
    return null;
  }

  return vendorConfig.constantPlu;
};

export const getQuantityBasedPlu = (plus: IPluWithQuantity[], quantity?: number): string | null => {
  if (!quantity) {
    return dlv(minQuantityPlu(plus), 'plu', null);
  }

  return dlv(plus.find(plu => plu.quantity === quantity)!, 'plu', null);
};

export const concatenateSizePlu = (plu: IPluWithSize): string | null => {
  if (!plu.comboPlu || !plu.comboSize) {
    return null;
  }

  return `${plu.comboPlu}-${plu.comboSize}`;
};

export const bestPlusForQuantity = (
  plus: IPluWithQuantity[],
  prices: IPrices,
  quantity: number
) => {
  // remove plus with no price and plus for quantities greater than the amount user is buying
  const relevantPlus = plus.filter(
    ({ plu, quantity: quantityForPlu }) => quantityForPlu <= quantity && plu in prices
  );
  // sort in descending order
  const sorted = relevantPlus.sort((left, right) => (left.quantity < right.quantity ? 1 : -1));
  // build a bucket of plus that sums to the user's selected quantity
  return sorted.reduce<IPluWithQuantity[]>((bucket, currentPlu) => {
    const remainingSpace = quantity - bucket.reduce((acc, { quantity: qty }) => acc + qty, 0);
    return bucket.concat(
      Array<IPluWithQuantity>(Math.floor(remainingSpace / currentPlu.quantity)).fill(currentPlu)
    );
  }, []);
};

interface IComputePlu {
  item: IBackendCartEntries | ICartEntry | IItem;
  modifier: IBackendCartEntries | ICartEntry | ISanityItemOptionModifier | IItemOptionModifier;
  vendor: PosVendors | null;
}

/**
 * Computes a Plu for a modifier that must be priced
 * using its parent's plu, i.e.
 * item - constantPlu 123
 * modifier - constantPlu 345
 *  returns 123-345
 *
 * currently this is only supported if:
 *   - the parent item has a constant plu
 *   - the modifier has a constant or multi-constant
 *     plu array with a length of 1
 *
 * @returns {string | null} the composite plu or null
 * if the criteria were not satisfied by the provided
 * arguments
 */
export const computeCompositeModifierPlu = ({
  item,
  modifier,
  vendor
}: IComputePlu): string | null => {
  const itemVendorConfig = getVendorConfig(item, vendor);

  // modifier composite plus can only
  // be computed for items with a`constantPlu`
  if (!itemVendorConfig || itemVendorConfig.pluType !== PluTypes.CONSTANT) {
    return null;
  }

  const modifierVendorConfig = getVendorConfig(modifier, vendor);

  if (!modifierVendorConfig) {
    return null;
  }

  if (modifierVendorConfig.pluType === PluTypes.SIZE_BASED) {
    logger.warn({ message: 'Unable to compute modifier PLU with PluType SizeBasedPlu', modifier });
    // TODO: determine what should happen here..
    return null;
  }

  if (modifierVendorConfig.pluType === PluTypes.PARENT_CHILD) {
    logger.warn({
      message: 'Unable to compute modifier PLU with PluType ParentChildPlu',
      modifier
    });
    // TODO: determine what should happen here..
    return null;
  }

  if (
    modifierVendorConfig.pluType === PluTypes.MULTI_CONSTANT &&
    modifierVendorConfig.multiConstantPlus !== null &&
    modifierVendorConfig.multiConstantPlus.length
  ) {
    const [{ plu }] = modifierVendorConfig.multiConstantPlus;

    return `${itemVendorConfig.constantPlu}-${plu}`;
  }

  if (
    modifierVendorConfig.pluType === PluTypes.CONSTANT &&
    modifierVendorConfig.constantPlu !== null
  ) {
    return `${itemVendorConfig.constantPlu}-${modifierVendorConfig.constantPlu}`;
  }

  return null;
};

export const computeSimpleModifierPlu = ({ modifier, vendor }: IComputePlu): string | null => {
  const modifierVendorConfig = getVendorConfig(modifier, vendor);

  if (modifierVendorConfig && modifierVendorConfig.pluType === PluTypes.CONSTANT) {
    return modifierVendorConfig.constantPlu;
  }

  return null;
};

export enum PluQualifier {
  ADD = 'ADD',
  HALF = '1/2',
  NO = 'NO'
}

// multi constant plus sometimes have qualifiers
// that express a quantity, i.e. NO or 1/2
// when an item option modifier has
// multi constant plus we should reduce the array
// of plus to a quantity to determine pricing
export function reduceMultiConstantPluQuantity(plus: IPluWithQuantity[]) {
  // if any NO plu is found this modifier
  // should not be considered "added"
  if (plus.find(({ qualifier }) => qualifier === PluQualifier.NO)) {
    return 0;
  }

  return plus.reduce((quantity, { qualifier, quantity: pluQuantity }) => {
    switch (qualifier) {
      case PluQualifier.HALF:
        return quantity + 0.5;
      case PluQualifier.ADD:
      default:
        return quantity + (pluQuantity || 0);
    }
  }, 0);
}
